import React, { useEffect, useState } from "react";
import api from "../api";
import {
  Lock, MessageCircle, ShieldCheck, RefreshCw, ExternalLink, CheckCircle2, AlertCircle,
  Radio, Sparkles,
} from "lucide-react";

const CH1        = (process.env.REACT_APP_TELEGRAM_CHANNEL_1 || "").trim();
const CH2        = (process.env.REACT_APP_TELEGRAM_CHANNEL_2 || "").trim();
const CH_BC      = (process.env.REACT_APP_TELEGRAM_BROADCAST_CHANNEL || "@turbogc_official").trim();
const CH1_LABEL  = process.env.REACT_APP_TELEGRAM_CHANNEL_1_LABEL || "News Channel";
const CH2_LABEL  = process.env.REACT_APP_TELEGRAM_CHANNEL_2_LABEL || "Community Chat";
const CH_BC_LBL  = process.env.REACT_APP_TELEGRAM_BROADCAST_LABEL || "TGC Broadcast · Announcements";

const joinUrl = (ch) => {
  if (!ch) return "";
  if (ch.startsWith("@")) return "https://t.me/" + ch.slice(1);
  if (/^-?\d+$/.test(ch)) return "";
  return "https://t.me/" + ch;
};

const VERIFY_TEXT =
  "Verification Step: To fully unlock your dashboard, secure your AI Agent, " +
  "and confirm your €100 TGC Airdrop allocation, you must join our TWO official network channels.";
const FAIL_TEXT = "Membership not verified. Please join both groups and try again.";

/**
 * Step-13 gate.
 *   • userRole === "admin"                         → bypass entirely
 *   • userRole === "biz"  (Merchant Retail/Ent.)   → BYPASS the 2-group check.
 *                                                    Show broadcast-channel subscribe panel
 *                                                    + auto-initialise Turbo AI Support.
 *   • userRole === "user" (Provider)               → enforce classic 2-group check.
 */
export default function TelegramGate({ children, userRole = "user" }) {
  const [status, setStatus]         = useState(null);
  const [verifying, setVerifying]   = useState(false);
  const [alertText, setAlertText]   = useState("");
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [tgIdInput, setTgIdInput]   = useState("");
  const [tgUsernameInput, setTgUsernameInput] = useState("");
  const [linking, setLinking]       = useState(false);
  const [bcAcked, setBcAcked]       = useState(localStorage.getItem("tgc_bc_ack") === "1");

  const load = async () => {
    try {
      const r = await api.get("/telegram/status");
      setStatus(r.data);
    } catch (_e) {
      setStatus({ configured: false, linked: false, verified: true, __err: true });
    }
  };
  useEffect(() => { load(); }, []);

  const link = async (e) => {
    e && e.preventDefault();
    setAlertText("");
    const id = parseInt(tgIdInput, 10);
    if (!id || id <= 0) { setAlertText("Введите корректный числовой Telegram user_id"); return; }
    setLinking(true);
    try {
      await api.post("/telegram/link", { telegram_user_id: id, telegram_username: tgUsernameInput || null });
      setShowLinkForm(false);
      await load();
    } catch (e2) {
      setAlertText(e2.response?.data?.detail || "Ошибка привязки");
    } finally { setLinking(false); }
  };

  const verify = async () => {
    setAlertText("");
    setVerifying(true);
    try {
      const r = await api.post("/telegram/verify");
      if (r.data?.verified) {
        setStatus((s) => ({ ...(s || {}), verified: true, verifiedAt: new Date().toISOString(), channelStatuses: r.data.channelStatuses }));
      } else {
        setAlertText(FAIL_TEXT);
        setStatus((s) => ({ ...(s || {}), verified: false, channelStatuses: r.data.channelStatuses }));
      }
    } catch (e) {
      setAlertText(e.response?.data?.detail || "Ошибка проверки");
    } finally { setVerifying(false); }
  };

  const ackBroadcast = () => {
    localStorage.setItem("tgc_bc_ack", "1");
    setBcAcked(true);
  };

  if (status === null) return null;

  // 1. Admin bypass, or gate not configured, or classic verify already passed
  if (userRole === "admin" || !status.configured || status.verified) {
    return <>{children}</>;
  }

  // 2. MERCHANT branch — Step 13: bypass 2-group verify, show Broadcast + AI init
  if (userRole === "biz") {
    if (bcAcked) return <>{children}</>;
    return (
      <div data-testid="telegram-gate-merchant"
        style={{ position: "fixed", inset: 0, zIndex: 9999,
                 background: "rgba(5, 9, 20, 0.94)", backdropFilter: "blur(12px)",
                 display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
        <div style={{ maxWidth: 620, width: "100%",
                      background: "linear-gradient(180deg, #0B1220 0%, #050914 100%)",
                      border: "1px solid #BC13FE", borderRadius: 18,
                      padding: 32, boxShadow: "0 30px 80px rgba(188,19,254,0.20)" }}>
          <div style={{ textAlign: "center", marginBottom: 22 }}>
            <div style={{ display: "inline-flex", padding: 14, background: "rgba(188,19,254,0.12)", borderRadius: "50%", marginBottom: 12 }}>
              <Radio size={30} color="#BC13FE" />
            </div>
            <h2 data-testid="tg-merchant-title" style={{ margin: 0, fontSize: 22, color: "#BC13FE" }}>
              Merchant Fast-Track · Broadcast Only
            </h2>
            <div style={{ fontSize: 12, color: "#8B94B8", marginTop: 8 }}>
              MERCHANT_RETAIL / MERCHANT_ENTERPRISE tier detected — no community-group check required.
            </div>
          </div>

          <div style={{ padding: 16, borderRadius: 12,
                        background: "linear-gradient(135deg, rgba(188,19,254,0.10), rgba(0,242,255,0.05))",
                        border: "1px solid rgba(188,19,254,0.35)", marginBottom: 14 }}>
            <div style={{ fontSize: 13, color: "#E7ECFF", lineHeight: 1.6 }}>
              Subscribe to our read-only <b style={{ color: "#BC13FE" }}>Broadcast Announcement Channel</b> to
              receive product updates, regulatory alerts, and settlement window notifications. No community chatter.
            </div>
          </div>

          <a href={joinUrl(CH_BC) || "#"} target="_blank" rel="noreferrer"
             data-testid="tg-join-broadcast"
             style={{ padding: "14px 16px", borderRadius: 10, textDecoration: "none",
                      background: "rgba(188,19,254,0.10)", border: "1px solid #BC13FE",
                      color: "#BC13FE", display: "flex", alignItems: "center", gap: 12,
                      marginBottom: 14 }}>
            <MessageCircle size={20} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 800, fontSize: 15 }}>{CH_BC_LBL}</div>
              <div style={{ fontSize: 11, opacity: 0.75 }}>{CH_BC || "@turbogc_official"}</div>
            </div>
            <ExternalLink size={16} />
          </a>

          <div style={{ padding: 14, borderRadius: 12,
                        background: "rgba(16,244,167,0.05)", border: "1px solid rgba(16,244,167,0.30)",
                        marginBottom: 18, display: "flex", gap: 10, alignItems: "flex-start" }}>
            <Sparkles size={18} color="#10F4A7" style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ fontSize: 12, color: "#E7ECFF", lineHeight: 1.6 }}>
              <b style={{ color: "#10F4A7" }}>Turbo AI Support</b> initialising in your Merchant Dashboard.
              Ask about fees, compliance, payroll, Web3 config, tax reserves — 24/7 in your local language.
              <div style={{ fontSize: 10, color: "#5F6884", marginTop: 4 }}>
                Powered by a self-learning RAG core (16+ seed manifests, TF-IDF vector index).
              </div>
            </div>
          </div>

          <button data-testid="tg-merchant-continue" onClick={ackBroadcast}
            style={{ width: "100%", padding: "14px 18px", borderRadius: 10, border: "none",
                     background: "linear-gradient(135deg, #BC13FE, #00F2FF)",
                     color: "#fff", fontWeight: 800, fontSize: 14, cursor: "pointer",
                     display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <Sparkles size={15} /> Continue to Merchant Dashboard
          </button>
        </div>
      </div>
    );
  }

  // 3. PROVIDER / regular user — classic two-group blocking modal
  return (
    <div data-testid="telegram-gate"
      style={{ position: "fixed", inset: 0, zIndex: 9999,
               background: "rgba(5, 9, 20, 0.94)", backdropFilter: "blur(12px)",
               display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ maxWidth: 560, width: "100%",
                    background: "linear-gradient(180deg, #0B1220 0%, #050914 100%)",
                    border: "1px solid #00F2FF", borderRadius: 18,
                    padding: 32, boxShadow: "0 30px 80px rgba(0,242,255,0.18)" }}>
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{ display: "inline-flex", padding: 14, background: "rgba(0,242,255,0.1)", borderRadius: "50%", marginBottom: 12 }}>
            <Lock size={30} color="#00F2FF" />
          </div>
          <h2 style={{ margin: 0, fontSize: 22, color: "#00F2FF" }}>Telegram Membership Required</h2>
        </div>

        <p style={{ color: "#C9D1E8", fontSize: 14, lineHeight: 1.6, textAlign: "center", marginBottom: 22 }}>{VERIFY_TEXT}</p>

        <div style={{ display: "grid", gap: 10, marginBottom: 16 }}>
          <ChannelButton testId="tg-join-channel-1" label={CH1_LABEL} handle={CH1} joinUrl={joinUrl(CH1)} statusPayload={status.channelStatuses?.channel1} />
          <ChannelButton testId="tg-join-channel-2" label={CH2_LABEL} handle={CH2} joinUrl={joinUrl(CH2)} statusPayload={status.channelStatuses?.channel2} />
        </div>

        {!status.linked && !showLinkForm && (
          <button onClick={() => setShowLinkForm(true)} data-testid="tg-open-link-form"
            style={{ width: "100%", padding: "12px 14px", marginBottom: 10, borderRadius: 10,
                     background: "rgba(0,242,255,0.06)", border: "1px solid #00F2FF", color: "#00F2FF",
                     fontSize: 13, fontWeight: 600, cursor: "pointer",
                     display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <MessageCircle size={14} /> Сначала: привязать ваш Telegram
          </button>
        )}

        {showLinkForm && (
          <form onSubmit={link} data-testid="tg-link-form"
            style={{ padding: 14, background: "rgba(0,242,255,0.04)", border: "1px solid rgba(0,242,255,0.25)", borderRadius: 10, marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: "#8B94B8", marginBottom: 8, lineHeight: 1.5 }}>
              Узнайте ваш Telegram user_id: откройте <a href="https://t.me/userinfobot" target="_blank" rel="noreferrer" style={{ color: "#00F2FF" }}>@userinfobot</a>, нажмите Start.
            </div>
            <input value={tgIdInput} onChange={(e) => setTgIdInput(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder="Ваш Telegram user_id" data-testid="tg-link-id-input"
              inputMode="numeric" autoFocus style={inputStyle} />
            <input value={tgUsernameInput} onChange={(e) => setTgUsernameInput(e.target.value)}
              placeholder="Telegram username (не обязательно)" data-testid="tg-link-username-input" style={inputStyle} />
            <div style={{ display: "flex", gap: 6 }}>
              <button type="button" onClick={() => setShowLinkForm(false)} style={{ ...btnGhost, flex: 1 }}>Отмена</button>
              <button type="submit" disabled={linking || !tgIdInput} style={{ ...btnPrimary, flex: 2 }} data-testid="tg-link-submit">
                {linking ? "Привязка…" : "Привязать"}
              </button>
            </div>
          </form>
        )}

        {status.linked && (
          <div style={{ fontSize: 11, color: "#10F4A7", marginBottom: 10, textAlign: "center" }} data-testid="tg-linked-info">
            <CheckCircle2 size={11} style={{ verticalAlign: "middle" }} /> Telegram привязан:
            <code style={{ background: "rgba(0,0,0,0.3)", padding: "2px 8px", borderRadius: 4, marginLeft: 6, color: "#00F2FF" }}>
              {status.telegram_username ? "@" + status.telegram_username : status.telegram_user_id}
            </code>
            {" · "}<button onClick={() => setShowLinkForm(true)} style={{ background: "transparent", border: "none", color: "#8B94B8", textDecoration: "underline", cursor: "pointer", fontSize: 11 }}>изменить</button>
          </div>
        )}

        <button onClick={verify} disabled={verifying || !status.linked}
          data-testid="tg-verify-membership"
          style={{ width: "100%", padding: "14px 18px", borderRadius: 10, marginBottom: 12,
                   background: status.linked ? "linear-gradient(135deg, #FFD700, #FF9F43)" : "#333",
                   color: "#050914", fontWeight: 800, fontSize: 14, border: "none",
                   cursor: status.linked ? "pointer" : "not-allowed",
                   display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          {verifying ? <><RefreshCw size={15} className="spin" /> Проверяем…</> :
            <><ShieldCheck size={15} /> Verify Membership</>}
        </button>

        {alertText && (
          <div data-testid="tg-verify-alert"
            style={{ padding: 12, background: "rgba(255,61,108,0.08)", border: "1px solid #FF3D6C", borderRadius: 8, color: "#FF3D6C", fontSize: 13, display: "flex", gap: 8, alignItems: "flex-start" }}>
            <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>{alertText}</span>
          </div>
        )}

        {status?.__err && (
          <div style={{ fontSize: 11, color: "#8B94B8", textAlign: "center", marginTop: 12 }}>
            Сервер верификации временно недоступен — доступ разрешён без проверки.
          </div>
        )}
      </div>

      <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function ChannelButton({ label, handle, joinUrl, statusPayload, testId }) {
  const ok = statusPayload?.ok === true;
  const failed = statusPayload && statusPayload.ok === false;
  return (
    <a href={joinUrl || "#"} target="_blank" rel="noreferrer"
      data-testid={testId}
      onClick={(e) => { if (!joinUrl) e.preventDefault(); }}
      style={{ padding: "14px 16px", borderRadius: 10, textDecoration: "none",
               background: ok ? "rgba(16,244,167,0.08)" : failed ? "rgba(255,61,108,0.06)" : "rgba(0,242,255,0.06)",
               border: `1px solid ${ok ? "#10F4A7" : failed ? "#FF3D6C" : "#00F2FF"}`,
               color: ok ? "#10F4A7" : failed ? "#FF3D6C" : "#00F2FF",
               display: "flex", alignItems: "center", gap: 10 }}>
      <MessageCircle size={18} />
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 14 }}>{label}</div>
        <div style={{ fontSize: 11, opacity: 0.75 }}>{handle || "не настроено"}</div>
      </div>
      {ok && <CheckCircle2 size={16} />}
      {failed && <AlertCircle size={16} />}
      {!statusPayload && joinUrl && <ExternalLink size={14} />}
    </a>
  );
}

const inputStyle = {
  width: "100%", padding: "10px 12px", marginBottom: 8, borderRadius: 8,
  background: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.1)",
  color: "#E7ECFF", fontSize: 13, outline: "none",
};
const btnPrimary = {
  padding: "10px 14px", borderRadius: 8, fontSize: 13, fontWeight: 700,
  background: "#00F2FF", color: "#050914", border: "none", cursor: "pointer",
};
const btnGhost = {
  padding: "10px 14px", borderRadius: 8, fontSize: 13,
  background: "transparent", color: "#8B94B8", border: "1px solid rgba(255,255,255,0.1)", cursor: "pointer",
};
